<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">VIN Tenant Id Configuration</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-card class="section-card mb-4">
      <v-card-title>VIN prefix to Tenant Id (brand abbreviation and region) rules</v-card-title>
      <v-card-text>
        <p class="text-body-2 text-grey-darken-1 mb-4">
          The CFES will evaluate the rules order by Rule Number, starting with the lowest numbered rule,
          to determine the Tenant (Brand / Country) by the first VIN pattern matched.
          The highest number that you can use for a rule is 32766.
        </p>

        <v-data-table
          dense
          item-key="id"
          :headers="vinBrandRegionRuleHeaders"
          :items="vinBrandRegionRuleSet"
          :items-per-page="-1"
          disable-sort
          hide-default-footer
          class="elevation-1"
        >
          <template v-slot:[`item.id`]="{ item }">
            <v-btn v-if="editable" size="small" color="error" variant="text" @click="delRule(item)" v-permission="['tenant:config:edit']">Delete</v-btn>
          </template>
          <template v-slot:[`item.ruleNum`]="{ item }">
            <v-text-field
              density="compact"
              v-model="item.ruleNum"
              :rules="ruleNumRules"
              :readonly="!editable"
              hide-details
              variant="underlined"
            />
          </template>
          <template v-slot:[`item.pattern`]="{ item }">
            <v-text-field
              density="compact"
              v-model="item.pattern"
              :readonly="!editable"
              hide-details
              variant="underlined"
            />
          </template>
          <template v-slot:[`item.brand`]="{ item }">
            <v-select
              density="compact"
              v-model="item.brand"
              :readonly="!editable"
              :items="brandSet"
              item-title="text"
              item-value="value"
              hide-details
              variant="underlined"
            />
          </template>
          <template v-slot:[`item.region`]="{ item }">
            <v-select
              density="compact"
              v-model="item.region"
              :readonly="!editable"
              :items="regionSet"
              hide-details
              variant="underlined"
            />
          </template>
          <template v-slot:[`item.updatedTime`]="{ item }">
            {{ formatDate(item.updatedTime) }}
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions v-if="editable" class="px-4 pb-4">
        <v-btn color="primary" variant="tonal" @click="addRule" v-permission="['tenant:config:edit']">Add Rule</v-btn>
        <v-btn color="primary" @click="updateRules" v-permission="['tenant:config:edit']">Update</v-btn>
        <span v-if="updateTooltipMessage" class="ml-4 text-warning">{{ updateTooltipMessage }}</span>
      </v-card-actions>
    </v-card>

    <!-- Confirm Dialog -->
    <v-dialog v-model="confirmDialog" max-width="400">
      <v-card>
        <v-card-title>Confirm</v-card-title>
        <v-card-text>{{ confirmMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete" v-permission="['tenant:config:edit']">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment-timezone'
import {
  vinTenantIdConfigurationGetVinConfig,
  vinTenantIdConfigurationUpdateVinConfig
} from '@/api/cfes/vinTenantIdConfiguration'
import { toast } from '@/utils/toast'

export default {
  data: () => ({
    vinBrandRegionRuleHeaders: [
      { title: 'Action', key: 'id', sortable: false, width: 100 },
      { title: 'Rule Number', key: 'ruleNum', sortable: false, width: 120 },
      { title: 'VIN Pattern', key: 'pattern', sortable: false, width: 200 },
      { title: 'Brand', key: 'brand', sortable: false, width: 180 },
      { title: 'Region', key: 'region', sortable: false, width: 120 },
      { title: 'Updated Time (UTC)', key: 'updatedTime', sortable: false, width: 180 }
    ],
    vinBrandRegionRuleSet: [],
    brandSet: [
      { text: 'A-Audi', value: 'A' },
      { text: 'S-Seat', value: 'S' },
      { text: 'C-Skoda', value: 'C' },
      { text: 'V-VW', value: 'V' },
      { text: 'N-VWN', value: 'N' },
      { text: 'V-VW-North America', value: 'VWNA' },
      { text: 'V-VW-Canada', value: 'VWCA' },
    ],
    regionSet: ['ECE', 'CHN', 'NAR'],
    ruleNumRules: [
      v => !!v || 'Rule Number is required',
      v => v && v < 32767 || 'Rule Number should less than 32767',
    ],
    updateTooltipMessage: '',
    editable: true,
    confirmDialog: false,
    confirmMessage: '',
    itemToDelete: null,
    originalRuleSet: []
  }),

  watch: {
    vinBrandRegionRuleSet: {
      handler(newVal) {
        if (this.originalRuleSet.length > 0 && JSON.stringify(newVal) !== JSON.stringify(this.originalRuleSet)) {
          this.updateTooltipMessage = 'Rules have changed, click update to save! Or refresh page to rollback.'
        } else {
          this.updateTooltipMessage = ''
        }
      },
      deep: true
    }
  },

  created() {
    // Check permission - for now default to editable
    // this.editable = this.$auth.hasPermi("tenant:config:edit")
    this.editable = true
    if (!this.editable) {
      this.vinBrandRegionRuleHeaders = [
        { title: 'Rule Number', key: 'ruleNum', sortable: false, width: 120 },
        { title: 'VIN Pattern', key: 'pattern', sortable: false, width: 200 },
        { title: 'Brand', key: 'brand', sortable: false, width: 180 },
        { title: 'Region', key: 'region', sortable: false, width: 120 },
        { title: 'Updated Time (UTC)', key: 'updatedTime', sortable: false, width: 180 }
      ]
    }
    this.listAll()
  },

  methods: {
    listAll() {
      vinTenantIdConfigurationGetVinConfig().then((response) => {
        this.vinBrandRegionRuleSet = response || []
        this.originalRuleSet = JSON.parse(JSON.stringify(this.vinBrandRegionRuleSet))
      })
    },

    addRule() {
      this.vinBrandRegionRuleSet.push({
        id: Date.now(), // temporary id for new rules
        ruleNum: '',
        pattern: '',
        brand: '',
        region: ''
      })
    },

    delRule(item) {
      this.itemToDelete = item
      this.confirmMessage = `Are you sure you want to delete rule ${item.pattern} => ${item.brand}, ${item.region}?`
      this.confirmDialog = true
    },

    confirmDelete() {
      if (this.itemToDelete) {
        this.vinBrandRegionRuleSet = this.vinBrandRegionRuleSet.filter(o => o.id !== this.itemToDelete.id)
        this.itemToDelete = null
      }
      this.confirmDialog = false
    },

    updateRules() {
      // Prepare data for update - remove id, createdTime, updatedTime and filter empty entries
      const reducedSet = JSON.parse(JSON.stringify(this.vinBrandRegionRuleSet))
        .map(u => {
          delete u.id
          delete u.createdTime
          delete u.updatedTime
          return u
        })
        .filter(o => Object.entries(o).length !== 0 && o.ruleNum !== '')

      vinTenantIdConfigurationUpdateVinConfig(reducedSet).then(() => {
        this.updateTooltipMessage = ''
        toast.success('Rule update succeed!')
        // Refresh the list
        this.listAll()
      }).catch(() => {
        toast.error('Rule update failed!')
      })
    },

    formatDate(value) {
      if (!value) return ''
      return moment.utc(value).format('YYYY-MM-DD HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}

:deep(.v-data-table-header th) {
  background-color: #f1f5f9;
  color: #111827;
  font-weight: 600;
}

:deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

:deep(tbody tr:hover) {
  background-color: #eef2ff;
}
</style>

